<template>
  <div class="employee-detail">
    <!-- Show header and menu only when not inspecting -->
    <template v-if="!showInspectForm">
      <EmployeePageHeader
        :employeeId="computedEmployeeId"
        :employeeData="employeeData"
        :status="employeeStatus"
        :name="employeeName"
        :avatar="employeeData?.avatar || ''"
        :title="employeePositionTitle"
        :archived="employeeData?.archived || false"
        :tracking="employeeData?.tracking !== false"
        :readiness="
          employeeData?.primary_readiness ?? employeeData?.current_readiness ?? 0
        "
        :last_month_readiness="employeeData?.last_month_readiness ?? null"
        :breadcrumbs="computedBreadcrumbs"
        :readinessRecalc="readinessRecalc"
        @employeeUpdated="handleEmployeeUpdate"
        @employeeStatusChanged="handleEmployeeStatusChanged"
        @employeeDeleted="handleEmployeeDeleted"
        @breadcrumb-click="handleBreadcrumbClick"
      />
      <EmployeeTabBar
        :employeeId="computedEmployeeId"
        :activeTab="selectedTab"
        :accountDetails="accountProductDetails"
        @tab-selected="onTabChange"
        @tabs-ready="onTabsReady"
      />
    </template>
    <!-- Show InspectToAwardForm as full page when inspecting -->
    <div v-if="showInspectForm && selectedResponsibility" class="inspect-form-page-wrapper">
      <InspectToAwardForm
        :breadcrumbsList="inspectionBreadcrumbs"
        :employee="formattedEmployee"
        :responsibility="formattedResponsibility"
        :inspectionData="inspectionData"
        :filterText="inspectionFilterText"
        :showNoRecords="showNoInspectionRecords"
        :showPagination="showInspectionPagination"
        :pagination="inspectionPagination"
        :chipText="inspectionChipText"
        :filterGroups="inspectionFilterGroups"
        :filterType="inspectionFilterType"
        :statusOptions="inspectionStatusOptions"
        :groupsItems="inspectionGroupsItems"
        :levelCounter="inspectionLevelCounter"
        :currentBadge="currentBadgeLevel"
        :readyForLevel="readyForBadgeLevel"
        :dot="inspectionDot"
        @onClickBreadcrumb="handleInspectionBreadcrumbClick"
        @badge-selected="handleBadgeSelectedFromInspection"
        @onSearch="handleInspectionSearch"
        @onClick="handleInspectionClick"
        @removeFilter="handleInspectionRemoveFilter"
        @filterTypes="handleInspectionFilterTypes"
        @onPaginationRequest="handleInspectionPagination"
        @clearSelection="handleInspectionClearSelection"
        @onCheckedItemsChanged="handleInspectionCheckedItemsChanged"
        @onPass="handleInspectionPass"
        @onFail="handleInspectionFail"
        @onNotApplicable="handleInspectionNotApplicable"
        @onClose="(row) => handleInspectionResetOrClose(row)"
      />
    </div>

    <!-- Show Employee Detail content when not inspecting -->
    <div v-else class="employee-detail-content">
      <!-- Dashboard Tab -->
      <div
        v-if="
          selectedTab === 'dashboard' &&
          employeeData &&
          shouldShowOrganizationEmployeeDashboardTab(showDashboardTab)
        "
        class="tab-content-wrapper"
      >
        <EmployeeReadinessDashboard
          :key="`dashboard-${computedEmployeeId}`"
        />
      </div>

      <!-- Path to Readiness Tab -->
      <div
        v-if="selectedTab === 'pathtoreadiness' && employeeData && readinessProductOn"
        class="tab-content-wrapper"
      >
        <NewEmployeePathToReadinessView
          :key="`pathtoreadiness-${computedEmployeeId}`"
          :employeeId="computedEmployeeId"
          formatChatterGivenAsIssued
          hide-header
          @employeeStatusChanged="handleEmployeeStatusChanged"
          @readiness-recalc-change="onReadinessRecalcChange"
          :read_only="selectedTab === 'pathtoreadiness' ? false : true"
        />
      </div>

      <!-- Responsibilities Tab - Same as Path to Readiness for now -->
      <div
        v-if="selectedTab === 'responsibilities' && employeeData"
        class="tab-content-wrapper"
      >
        <NewEmployeePathToReadinessView
          :key="`responsibilities-${computedEmployeeId}`"
          :employeeId="computedEmployeeId"
          hide-header
          hide-inspect
          @employeeStatusChanged="handleEmployeeStatusChanged"
          @readiness-recalc-change="onReadinessRecalcChange"
        />
      </div>

      <!-- Badges Tab -->
      <div
        v-if="selectedTab === 'badges' && employeeData && readinessProductOn"
        class="tab-content-wrapper"
      >
        <EmployeeBadges
          ref="employeeBadgesRef"
          :key="`badges-${computedEmployeeId}`"
          @employeeUpdated="handleEmployeeUpdate"
          @employeeStatusChanged="handleEmployeeStatusChanged"
          @issue-badge-clicked="handleIssueBadgeClick"
          @appoint-badge-clicked="handleAppointBadgeClick"
          @remove-badge="handleRemoveBadgeClick"
          @openGiveBadgeDialogFromInspection="handleOpenGiveBadgeDialogFromBadgesInspection"
        />
      </div>

      <!-- Positions History Tab -->
      <div
        v-if="selectedTab === 'positionshistory' && employeeData"
        class="tab-content-wrapper"
      >
        <EmployeeAssignmentView
          ref="employeeAssignmentViewRef"
          :key="`positionshistory-${computedEmployeeId}`"
          @employeeUpdated="handleEmployeeUpdate"
          @employeeStatusChanged="handleEmployeeStatusChanged"
        />
      </div>
    </div>

    <!-- Badge Issuance Workflow -->
    <!-- Step 1: Pick Responsibility Dialog -->
    <PickResponsibilityToIssueABadgeDialog
      v-model="showPickResponsibilityDialog"
      :items="availableResponsibilities"
      :hasMoreData="hasMoreResponsibilities"
      :isLoadingMore="isLoadingResponsibilities"
      @next="handleResponsibilitySelected"
      @close="handleClosePickResponsibility"
      @search="handleSearchResponsibilities"
      @load-more="handleLoadMoreResponsibilities"
    />


    <!-- Step 3: Give Badge Dialog (when responsibility has NO active checkpoints or when level picked from inspection dropdown) -->
    <GiveBadgeDialog
      v-model="showGiveBadgeDialog"
      :mode="appointMode ? 'appoint' : 'give'"
      :description="appointMode ? '' : giveBadgeDescription"
      :employeeName="employeeName"
      :responsibilityName="selectedResponsibility?.name || ''"
      :canNotify="canNotifyEmployee"
      :defaultLevel="selectedBadgeLevel"
      :currentBadgeLevel="currentBadgeLevelForGiveDialog"
      :lockedLevel="giveBadgeDialogLockedLevel"
      @give-badge="handleGiveBadge"
      @appoint-badge="handleAppointBadge"
      @close="handleCloseGiveBadge"
    />

    <!-- Has Master Badge: employee already has Master for this responsibility -->
    <HasMasterBadgeDialog
      :show="showHasMasterBadgeDialog"
      @close="showHasMasterBadgeDialog = false"
    />

    <!-- Upgrade Badge: no checkpoints + current badge is not Master -->
    <UpgradeBadgeDialog
      :value="showUpgradeBadgeDialog"
      :employeeName="employeeName"
      :responsibilityName="selectedResponsibilityForUpgrade?.name || ''"
      :canNotify="canNotifyEmployee"
      :currentBadgeLevel="upgradeCurrentBadgeLevel"
      @input="showUpgradeBadgeDialog = $event"
      @give-badge="handleGiveBadgeFromUpgradeDialog"
      @close="closeUpgradeBadgeModal"
    />

    <!-- Reason Modal for Fail and Not Applicable -->
    <ReasonModal
      v-if="reasonModal.show"
      :showDialog="reasonModal.show"
      :title="reasonModal.config.title"
      :description="reasonModal.config.description"
      :placeHolder="reasonModal.config.placeHolder"
      :btnLabel="reasonModal.config.btnLabel"
      :btnDanger="reasonModal.config.btnDanger"
      :btnColor="reasonModal.config.btnColor"
      :value="reason"
      @input="(val) => reason = val"
      @onAction="handleReasonAction"
      @onClose="closeReasonModal"
    />

    <!-- Remove Badge Dialog -->
    <RemoveBadgeDialog
      v-model="showRemoveBadgeDialog"
      :badgeName="selectedBadgeForRemoval?.badgeName || selectedBadgeForRemoval?.level || ''"
      :responsibilityName="selectedBadgeForRemoval?.responsibilityName || selectedBadgeForRemoval?.responsibility?.name || ''"
      :employeeName="employeeName || employeeData?.name || ''"
      @remove="handleConfirmRemoveBadge"
      @close="handleCloseRemoveBadgeDialog"
    />

    <!-- Reset Evaluation Confirmation (when user clicks Reset on a checkpoint) -->
    <ConfirmationDialog
      :dialog="showResetConfirmDialog"
      @confirm="handleConfirmResetEvaluation"
      @cancel="showResetConfirmDialog = false"
      :icon="'alert-icon-red.svg'"
    >
      <template v-slot:title>Reset Evaluation</template>
      <template v-slot:content>
        Are you sure you want to reset this evaluation?
      </template>
    </ConfirmationDialog>

    <!-- Award Badge Dialog (when employee becomes ready for a level after passing inspection) -->
    <AwardBadgeDialog
      :isVisible="showAwardBadgeDialog"
      :employee="awardBadgeEmployee"
      :level="awardBadgeLevel"
      :currentEmployee="employeeData || null"
      :responsibilityName="formattedResponsibility?.responsibilityName || selectedResponsibility?.name || ''"
      @close="closeAwardBadgeDialog"
      @skip="skipAwardBadge"
      @activate="activateAwardBadge"
    />
  </div>
</template>

<script setup>
import { normalizeBadgeLevel } from "@components/utils/normalizeBadgeLevel";
import {
  defineProps,
  ref,
  computed,
  onMounted,
  watch,
  getCurrentInstance,
  nextTick,
} from "vue";
import EmployeePageHeader from "@components/EmployeePageHeader/EmployeePageHeader.vue";
import EmployeeTabBar from "@components/EmployeeTabBar/EmployeeTabBar.vue";
import NewEmployeePathToReadinessView from "@/views/DetailEmployeeViews/NewEmployeePathToReadinessView.vue";
import EmployeeBadges from "@components/EmployeeBadges/EmployeeBadges.vue";
import EmployeeAssignmentView from "@/views/DetailEmployeeViews/EmployeeAssignmentView.vue";
import PickResponsibilityToIssueABadgeDialog from "@components/PickResponsibilityToIssueABadgeDialog/PickResponsibilityToIssueABadgeDialog.vue";
import InspectToAwardForm from "@components/InspectToAwardForm/InspectToAwardForm.vue";
import GiveBadgeDialog from "@components/GiveBadgeDialog/GiveBadgeDialog.vue";
import HasMasterBadgeDialog from "@components/HasMasterBadgeDialog/HasMasterBadgeDialog.vue";
import { getBadgeLevelCode } from "@components/utils/badgeLevelLabels";
import UpgradeBadgeDialog from "@components/UpgradeBadgeDialog/UpgradeBadgeDialog.vue";
import ReasonModal from "@components/ReasonModal/ReasonModal.vue";
import RemoveBadgeDialog from "@components/RemoveBadgeDialog/RemoveBadgeDialog.vue";
import AwardBadgeDialog from "@components/AwardBadgeDialog/AwardBadgeDialog.vue";
import ConfirmationDialog from "@components/ResponsibilitiesPicker/ConfirmationDialog.vue";
import {
  getEmployeeByIdV2,
  getListofResponsibilityForBadges,
  createProficiencyBadge,
  getResponsibilityIdV2,
  getResponsibilityChecklistV2,
  getListActiveCheckpointsEvalV2,
  passCheckPointEvaluation,
  failCheckPointEvaluation,
  notApplicableCheckPointEvaluation,
  resetCheckPointEvaluation,
  deleteResponsibilityBadge,
  getEmployeeBadges,
} from "@/api";
import {
  badgeMatchesResponsibility,
  pickBadgeForResponsibility,
  mapBadgeRowToInspectionChip,
} from "@/utils/inspectionBadgeSync";
import { resolveHasActiveCheckpoints } from "@components/EvaluationGrandBadge/badgeIssueRules";
import {
  extractCreatedProficiencyBadgeId,
  proficiencyCheckpointErrorMessage,
} from "@/utils/proficiencyBadgeHelpers";
import { useEmployeeStore } from "@/store/pinia/employee";
import { refreshPositionsHistoryAssignmentsIfActive as refreshPositionsHistoryAssignmentsIfActiveHelper } from "./refreshPositionsHistoryAssignments";
import EmployeeReadinessDashboard from "@components/EmployeeReadinessDashboard/EmployeeReadinessDashboard.vue";
import { LD_FLAG_IDEA_MDP_609 } from "@components/constants/launchDarklyFlagKeys.js";
import { buildAppointBadgePayload, resolveAppointedDot } from "@components/utils/appointedBadge";
import {
  getOrganizationEmployeeDefaultQueryTab,
  normalizeOrganizationEmployeeQueryTab,
  ORG_EMPLOYEE_ASSIGNMENT_TAB,
  shouldShowOrganizationEmployeeDashboardTab,
  shouldShowOrganizationEmployeeReadinessTabs,
} from "@/utils/organizationEmployeeDetailTabs";

const props = defineProps({
  employeeId: {
    type: [Number, String],
    default: null,
  },
  breadcrumbsList: {
    type: Array,
    default: () => [],
  },
});

const instance = getCurrentInstance();
const proxy = instance?.proxy;
const route = proxy?.$route;

const emit = defineEmits(["backToList", "employeeUpdated", "breadcrumb-click"]);

const selectedTab = ref(getOrganizationEmployeeDefaultQueryTab());
const showDashboardTab = ref(false);
const organizationEmployeeQueryTabsReady = ref(false);
const employeeData = ref(null);
const accountId = ref(null);
const employeeId = ref(null);
const employeeStore = useEmployeeStore();
const employeeAssignmentViewRef = ref(null);
const readinessRecalc = ref({ pending: false, failed: false });
const onReadinessRecalcChange = (state) => {
  readinessRecalc.value = state;
};

const accountProductDetails = computed(() => proxy?.$root?.currentAccountDetails || {});

const readinessProductOn = computed(() =>
  shouldShowOrganizationEmployeeReadinessTabs(accountProductDetails.value)
);

/** Refetch position assignments when the Positions History tab is visible (header updates from store; table uses its own API cache). */
async function refreshPositionsHistoryAssignmentsIfActive() {
  await refreshPositionsHistoryAssignmentsIfActiveHelper(
    selectedTab.value,
    employeeAssignmentViewRef.value
  );
}

// Badge Issuance Workflow State
const isProcessingStatusChange = ref(false);
const isProcessingUpdate = ref(false);
const showPickResponsibilityDialog = ref(false);
const showInspectForm = ref(false);
const showGiveBadgeDialog = ref(false);
const giveBadgeDialogLockedLevel = ref(null);
const giveBadgeOpenedFromBadgesInspection = ref(false);
const appointMode = ref(false);
const showHasMasterBadgeDialog = ref(false);
const showUpgradeBadgeDialog = ref(false);
const selectedResponsibilityForUpgrade = ref(null);
const upgradeCurrentBadgeLevel = ref("apprentice");
const selectedResponsibility = ref(null);
const availableResponsibilities = ref([]);
const hasMoreResponsibilities = ref(false);
const isLoadingResponsibilities = ref(false);
const responsibilitySearchTerm = ref("");
const responsibilityPage = ref(1);

// Inspection Form State
const inspectionData = ref([]);
/**
 * The evaluation API exposes the outcome as `inspection_status` on newer
 * records and `status`/`evaluation_status` on older ones. Prefer whichever is
 * present so a passed checkpoint is not rendered as Pending.
 */
const resolveCheckpointOutcome = (item) =>
  item?.status || item?.inspection_status || item?.evaluation_status || "";
const inspectionFilterText = ref("");
const showNoInspectionRecords = ref(false);
const showInspectionPagination = ref(false);
const inspectionPagination = ref({ total: 0, page: 1, perPage: 10 });
const inspectionChipText = ref([]);
const inspectionFilterGroups = ref([]);
const inspectionFilterType = ref("Checkpoint");
const inspectionStatusOptions = ref([
  "Not Yet Passed",
  "Only Passed",
  "Only Failed",
  "Only Not Yet Evaluated",
  "Only Not Applicable",
  "Only Reset",
]);
const inspectionGroupsItems = ref([
  {
    name: "Evaluation Status",
    groups: [
      {
        type: "check",
        label: "Status",
        items: [
          { label: "Not Yet Passed", value: "Not Yet Passed" },
          { label: "Only Passed", value: "Only Passed" },
          { label: "Only Failed", value: "Only Failed" },
          { label: "Only Not Yet Evaluated", value: "Only Not Yet Evaluated" },
          { label: "Only Not Applicable", value: "Only Not Applicable" },
          { label: "Only Reset", value: "Only Reset" },
        ],
      },
    ],
  },
]);
const inspectionLevelCounter = ref({
  Apprentice: 0,
  Professional: 0,
  Coach: 0,
  Master: 0,
});
const currentBadgeLevel = ref(null);
/** Computed: highest level for which all checkpoints are passed or N/A (used to show Award Badge pop-up when level is complete). */
const readyForBadgeLevel = computed(() => {
  const data = inspectionData.value || [];
  const checkpointsByLevel = {
    Apprentice: [],
    Professional: [],
    Coach: [],
    Master: [],
  };
  data.forEach((checkpoint) => {
    const level = checkpoint.level;
    if (checkpointsByLevel[level]) {
      checkpointsByLevel[level].push(checkpoint);
    }
  });
  const levels = ["Master", "Coach", "Professional", "Apprentice"];
  for (const level of levels) {
    const checkpoints = checkpointsByLevel[level];
    if (!checkpoints || checkpoints.length === 0) continue;
    const allComplete = checkpoints.every(
      (cp) =>
        cp.evaluation_status === "passed" ||
        cp.evaluation_status === "not_applicable"
    );
    if (allComplete) return level;
  }
  return "Novice";
});
const inspectionDot = ref("Good Standing");
const formattedResponsibility = ref(null);
const formattedEmployee = ref(null);
const inspectionBreadcrumbs = ref([
  { text: "Employee", disabled: false },
  { text: "Issue Badge", disabled: true },
]);

// Reason modal state for Fail and Not Applicable
const reasonModal = ref({
  show: false,
  type: null,
  config: {},
});
const modalConfigs = {
  fail: {
    title: "Add Criteria Failed Reason",
    description: "Please provide a reason why this criteria was not met.",
    placeHolder: "Explain why the criteria was not fulfilled or provide specific feedback.",
    btnLabel: "Fail",
    btnDanger: true,
    btnColor: null,
  },
  notApplicable: {
    title: "Mark Criteria as Not Applicable",
    description: "Provide a reason why this criteria is not applicable.",
    placeHolder: "Explain why the criteria is not applicable.",
    btnLabel: "Mark as Not Applicable",
    btnDanger: false,
    btnColor: "#666666",
  },
};
const reason = ref("");
const selectedCheckpoint = ref(null);

// Reset evaluation state (when user clicks Reset on a checkpoint)
const showResetConfirmDialog = ref(false);
const checkpointToReset = ref(null);

// Award Badge Dialog state (when employee becomes ready for a level after passing inspection)
const showAwardBadgeDialog = ref(false);
const awardBadgeLevel = ref("");
const awardBadgeEmployee = computed(() => ({
  name: [formattedEmployee.value?.firstName, formattedEmployee.value?.lastName].filter(Boolean).join(" ") || employeeName.value || "Employee",
  avatar: formattedEmployee.value?.photoUrl || employeeData.value?.avatar || "",
}));

// Remove Badge Dialog State
const showRemoveBadgeDialog = ref(false);
const selectedBadgeForRemoval = ref(null);
const employeeBadgesRef = ref(null);

// Give Badge Dialog State
const selectedBadgeLevel = ref("apprentice");
const canNotifyEmployee = computed(() => {
  const access = employeeData.value?.access;
  if (access == null) return false;
  if (access.is_suspended === true) return false;
  if (access.has_access === false) return false;
  return true;
});
const giveBadgeDescription = computed(() => {
  if (selectedResponsibility.value?.name) {
    return `Responsibility ${selectedResponsibility.value.name} has no Readiness Checklist therefore you can only Give (Instead of Awarding) a Badge to employee ${employeeName.value}.`;
  }
  return "";
});

// Current badge level for the selected responsibility (lowercase), so Give Badge shows upgrade options only
const currentBadgeLevelForGiveDialog = computed(() => {
  if (!selectedResponsibility.value?.id || !employeeData.value?.badges?.length) return null;
  const badge = employeeData.value.badges.find(
    (b) => b.is_current && badgeMatchesResponsibility(b, selectedResponsibility.value.id)
  );
  if (!badge) return null;
  const raw = badge.level ?? badge.currentBadge?.level ?? "novice";
  const normalized = getBadgeLevelCode(raw);
  return ["apprentice", "professional", "coach", "master"].includes(normalized) ? normalized : null;
});

const computedEmployeeId = computed(() => {
  return (
    props.employeeId ||
    route?.params?.id ||
    route?.params?.employeeId ||
    route?.query?.employeeId ||
    employeeData.value?.id ||
    null
  );
});

const employeeName = computed(() => {
  if (employeeData.value?.first_name && employeeData.value?.last_name) {
    return `${employeeData.value.first_name} ${employeeData.value.last_name}`;
  }
  return employeeData.value?.first_name || employeeData.value?.last_name || "";
});

const employeeStatus = computed(() => {
  if (employeeData.value?.archived) return "archived";
  return employeeData.value?.current_status || employeeData.value?.status || "";
});

const employeePositionTitle = computed(() => {
  // Get the most up-to-date employee data from either local ref or store
  const currentEmployee = employeeData.value || employeeStore.currentEmployee;
  
  if (!currentEmployee) {
    return "";
  }
  
  // Don't show position for terminated employees
  const status = currentEmployee?.current_status || currentEmployee?.status || "";
  if (status.toLowerCase() === "terminated") {
    return "";
  }
  
  // Only show the current primary position — do not fall back to the legacy
  // `title` field, which mirrors a past assignment and goes stale after reactivation.
  return currentEmployee?.primary_position?.name || "";
});

const computedBreadcrumbs = computed(() => {
  if (props.breadcrumbsList && props.breadcrumbsList.length > 0) {
    return props.breadcrumbsList;
  }

  const fromReadiness = route?.query?.fromReadiness === "1";
  const currentPath = route?.path || "";
  const employeesHref = currentPath.includes("/organization")
    ? "/organization/employees"
    : "/organization/employees";

  if (fromReadiness) {
    return [
      { text: "Readiness", disabled: false, href: "/new-readiness" },
      { text: "Employees", disabled: false, href: "/organization/employees" },
      {
        text: employeeName.value || "Employee",
        disabled: true,
        truncate: true,
      },
    ];
  }

  return [
    {
      text: "Employees",
      disabled: false,
      href: employeesHref,
    },
    {
      text: "Employee",
      disabled: true,
    },
  ];
});

function applyOrganizationEmployeeQueryTab(employeeTabFromQuery) {
  const normalized = normalizeOrganizationEmployeeQueryTab(
    employeeTabFromQuery,
    showDashboardTab.value,
    accountProductDetails.value
  );
  selectedTab.value = normalized;
  return normalized;
}

function syncEmployeeTabFromQuery(employeeTabFromQuery) {
  const normalized = applyOrganizationEmployeeQueryTab(employeeTabFromQuery);
  const isNewOrganization =
    route?.name === "NewOrganization" || route?.path?.includes("/organization");
  if (
    isNewOrganization &&
    proxy.$router &&
    String(employeeTabFromQuery || "").toLowerCase() !== normalized
  ) {
    const employeeId = computedEmployeeId.value || route?.query?.employeeId;
    const query = {
      employeeId,
      employeeTab: normalized,
    };
    if (route?.query?.fromReadiness === "1") {
      query.fromReadiness = "1";
    }
    proxy.$router
      .replace({
        name: "NewOrganization",
        params: { tab: "employees" },
        query,
      })
      .catch(() => {});
  }
}

function onTabsReady(visibleTabKeys) {
  if (
    readinessProductOn.value &&
    selectedTab.value === ORG_EMPLOYEE_ASSIGNMENT_TAB &&
    !visibleTabKeys.includes(ORG_EMPLOYEE_ASSIGNMENT_TAB)
  ) {
    onTabChange("badges");
  }
}

function onTabChange(tabKey) {
  selectedTab.value = tabKey;
  if (proxy.$router && route) {
    // Check if we're on the new organization route
    const isNewOrganization = route?.name === "NewOrganization" ||
                               route?.path?.includes("/organization");

    if (isNewOrganization) {
      // For new organization, navigate to NewOrganization route with query parameters
      const employeeId = computedEmployeeId.value || route?.query?.employeeId;
      const query = {
        employeeId: employeeId,
        employeeTab: tabKey,
      };
      if (route?.query?.fromReadiness === "1") {
        query.fromReadiness = "1";
      }
      proxy.$router
        .replace({
          name: "NewOrganization",
          params: { tab: "employees" },
          query,
        })
        .catch(() => {});
    } else {
      // For old organization routes, use the existing behavior
      proxy.$router
        .replace({
          ...route,
          query: {
            ...route.query,
            employeeTab: tabKey,
          },
        })
        .catch(() => {});
    }
  }
}

function handleBreadcrumbClick(breadcrumb) {
  if (route?.query?.fromReadiness === "1") {
    if (breadcrumb.text === "Readiness" || breadcrumb.text === "Employees") {
      proxy.$router?.push("/new-readiness").catch(() => {});
      return;
    }
  }
  if (breadcrumb.text === "Employees") {
    emit("backToList");
  }
}

async function fetchEmployeeData(forceRefresh = false) {
  try {
    if (!accountId.value || !employeeId.value) {
      console.warn("Missing accountId or employeeId:", {
        accountId: accountId.value,
        employeeId: employeeId.value,
      });
      return;
    }

    // If forceRefresh is true, use store's fetchCurrentEmployee to bypass cache
    if (forceRefresh) {
      const freshData = await employeeStore.fetchCurrentEmployee(
        employeeId.value,
        true
      );
      if (freshData) {
        employeeData.value = { ...freshData };
        if (proxy.$store) {
          proxy.$store.commit("employeeStore/setCurrentEmployee", freshData);
        }
        employeeStore.setCurrentEmployee(freshData);
        await nextTick();
        return;
      }
    }

    const { data } = await getEmployeeByIdV2({
      account_id: accountId.value,
      employeeId: employeeId.value,
    });

    if (data) {
      // Create a new object reference to ensure reactivity
      employeeData.value = { ...data };
      // Update both Vuex and Pinia stores so that EmployeeHorizontalSideBar can access the correct employee
      if (proxy.$store) {
        proxy.$store.commit("employeeStore/setCurrentEmployee", data);
      }
      // Also update Pinia store
      employeeStore.setCurrentEmployee(data);
      
      // Wait for Vue to recognize the change
      await nextTick();

      // If readiness is 0, it might need recalculation, but don't force it here
      // The EmployeeHorizontalSideBar component will handle recalculation if needed
      // Just ensure we're using the most up-to-date value
    } else {
      console.warn("No employee data found for ID:", employeeId.value);
      employeeData.value = null;
    }
  } catch (error) {
    console.error("Error fetching employee data:", error);
    employeeData.value = null;
  }
}

async function handleEmployeeUpdate(payload) {
  if (isProcessingUpdate.value || isProcessingStatusChange.value) return;
  isProcessingUpdate.value = true;
  try {
    // Prefer payload from sidebar when present to avoid a duplicate GET
    const freshData =
      payload && payload.id
        ? payload
        : await employeeStore.fetchCurrentEmployee(computedEmployeeId.value, true);

    if (freshData) {
      employeeData.value = { ...freshData };

      if (proxy.$store) {
        proxy.$store.commit("employeeStore/setCurrentEmployee", freshData);
      }
      employeeStore.setCurrentEmployee(freshData);

      await nextTick();
    } else {
      await fetchEmployeeData();
    }
  } catch (error) {
    console.error("Error updating employee data:", error);
    await fetchEmployeeData();
  } finally {
    isProcessingUpdate.value = false;
  }

  emit("employeeUpdated");
}

async function handleEmployeeStatusChanged(payload) {
  if (isProcessingStatusChange.value) return;
  isProcessingStatusChange.value = true;
  try {
    // Prefer payload from terminate/reactivate handlers — avoid a second GET (ER-9611)
    const freshData =
      payload && payload.id
        ? payload
        : await employeeStore.fetchCurrentEmployee(computedEmployeeId.value, true);

    if (freshData) {
      // CRITICAL: Normalize status field - ensure both status and current_status are set
      // This ensures child component watchers trigger properly
      const normalizedEmployeeData = {
        ...freshData,
        status: freshData.status || freshData.current_status,
        current_status: freshData.current_status || freshData.status,
        // Ensure primary_positions is cleared for terminated employees
        primary_positions: freshData.primary_positions || [],
      };

      // Check status from the fresh API data
      const statusToCheck = (
        normalizedEmployeeData.current_status ||
        normalizedEmployeeData.status ||
        ""
      ).toLowerCase();

      // If status is terminated, ensure assignments are cleared
      if (statusToCheck === "terminated") {
        // Force clear primary_positions to ensure UI updates correctly
        normalizedEmployeeData.primary_positions = [];
        normalizedEmployeeData.primary_position = null;
      }

      // Create new object reference to ensure reactivity
      employeeData.value = { ...normalizedEmployeeData };

      // Update stores with normalized data
      if (proxy.$store) {
        proxy.$store.commit("employeeStore/setCurrentEmployee", normalizedEmployeeData);
      }
      employeeStore.setCurrentEmployee(normalizedEmployeeData);

      // Wait for Vue to recognize the change
      await nextTick();

      await refreshPositionsHistoryAssignmentsIfActive();
      // Child Path-to-Readiness views react via store/employeeData watchers.
      // Do not call their handleEmployeeStatusChanged here — that re-fetches and
      // re-emits employeeStatusChanged, causing an infinite GET loop (ER-9611).
    } else {
      // Fallback to direct API call if store method fails
      await fetchEmployeeData();
      await refreshPositionsHistoryAssignmentsIfActive();
    }
  } catch (error) {
    console.error("Error updating employee status:", error);
    // Fallback to direct API call on error
    await fetchEmployeeData();
    await refreshPositionsHistoryAssignmentsIfActive();
  } finally {
    isProcessingStatusChange.value = false;
  }
}

function handleEmployeeDeleted() {
  emit("backToList");
}

// Badge Issuance Workflow Handlers
function handleIssueBadgeClick() {
  appointMode.value = false;
  showPickResponsibilityDialog.value = true;
  responsibilityPage.value = 1;
  responsibilitySearchTerm.value = "";
  availableResponsibilities.value = [];
  loadResponsibilities();
}

function handleAppointBadgeClick() {
  handleIssueBadgeClick();
  appointMode.value = true;
}

async function loadResponsibilities(isLoadMore = false) {
  if (!accountId.value) return;
  
  try {
    isLoadingResponsibilities.value = true;
    const limit = 10;
    const offset = isLoadMore ? (responsibilityPage.value - 1) * limit : 0;
    
    const response = await getListofResponsibilityForBadges({
      accountId: accountId.value,
      limit,
      offset,
      name: responsibilitySearchTerm.value,
      status: "active",
    });
    
    const responsibilities = response.data?.results || [];
    const newItems = responsibilities.map((item) => ({
      id: item.id,
      code: item.code_str?.match(/\d+/)?.[0] || item.code,
      name: item.name,
      status: item.status,
    }));
    
    if (isLoadMore) {
      availableResponsibilities.value = [...availableResponsibilities.value, ...newItems];
    } else {
      availableResponsibilities.value = newItems;
    }
    
    hasMoreResponsibilities.value = response.data?.next !== null;
  } catch (error) {
    console.error("Error loading responsibilities:", error);
  } finally {
    isLoadingResponsibilities.value = false;
  }
}

function handleSearchResponsibilities(searchTerm) {
  responsibilitySearchTerm.value = searchTerm;
  responsibilityPage.value = 1;
  loadResponsibilities();
}

function handleLoadMoreResponsibilities() {
  responsibilityPage.value += 1;
  loadResponsibilities(true);
}

function handleClosePickResponsibility() {
  appointMode.value = false;
  showPickResponsibilityDialog.value = false;
  selectedResponsibility.value = null;
}

async function handleResponsibilitySelected(payload) {
  if (!payload?.responsibility) return;
  
  selectedResponsibility.value = payload.responsibility;
  showPickResponsibilityDialog.value = false;
  
  try {
    proxy.$loader?.show();

    // Resolve employee's current badge for this responsibility (for Master / Upgrade / Give decision)
    let badges = employeeData.value?.badges || [];
    if (badges.length === 0 && accountId.value && computedEmployeeId.value) {
      try {
        const badgesRes = await getEmployeeBadges(
          accountId.value,
          computedEmployeeId.value
        );
        const list = badgesRes?.data?.results ?? badgesRes?.data ?? [];
        badges = Array.isArray(list) ? list : [];
        if (employeeData.value && badges.length) {
          employeeData.value = { ...employeeData.value, badges };
        }
      } catch (e) {
        console.warn("Could not load employee badges for modal decision:", e);
      }
    }
    const badgeMatchCtx = {
      responsibilityCode:
        selectedResponsibility.value?.code != null &&
        selectedResponsibility.value?.code !== ""
          ? `#${String(selectedResponsibility.value.code).replace(/^#/, "")}`
          : "",
    };
    const currentBadgeData = pickBadgeForResponsibility(
      badges,
      selectedResponsibility.value.id,
      badgeMatchCtx
    );
    let currentLevelLower = null;
    if (currentBadgeData) {
      const raw = currentBadgeData.level ?? currentBadgeData.currentBadge?.level;
      currentLevelLower =
        getBadgeLevelCode(raw) ??
        (typeof raw === "string" ? raw.toLowerCase() : getLevelName(raw).toLowerCase());
    }

    // Appoint skips the Award/inspection routing; a current Architect disables it inside the dialog.
    if (appointMode.value) {
      showGiveBadgeDialog.value = true;
      proxy.$loader?.hide();
      return;
    }

    // Has Master: when employee has a current Master badge for the responsibility → show HasMasterBadgeDialog
    if (currentLevelLower === "master") {
      showHasMasterBadgeDialog.value = true;
      proxy.$loader?.hide();
      return;
    }

    // Responsibility checkpoint count (for no-checkpoints vs inspect)
    const respDetailResponse = await getResponsibilityIdV2(
      accountId.value,
      selectedResponsibility.value.id,
      "responsibility"
    );
    const respDetail = respDetailResponse?.data ?? respDetailResponse;
    const hasActiveCheckpoints = resolveHasActiveCheckpoints({
      responsibility: respDetail,
    });

    // No active checkpoints: non-Master → UpgradeBadgeDialog; else GiveBadgeDialog
    if (!hasActiveCheckpoints) {
      if (
        currentLevelLower &&
        ["apprentice", "professional", "coach"].includes(currentLevelLower)
      ) {
        selectedResponsibilityForUpgrade.value = selectedResponsibility.value;
        upgradeCurrentBadgeLevel.value = currentLevelLower;
        showUpgradeBadgeDialog.value = true;
        proxy.$loader?.hide();
        return;
      }
      showGiveBadgeDialog.value = true;
      proxy.$loader?.hide();
      return;
    }

    // Has active checkpoints: load inspection data and open right pane in EmployeeBadges
    await loadInspectionData();

    // Load responsibility checklist for the right pane
    let responsibilityCheckpoints = [];
    try {
      const checklistResponse = await getResponsibilityChecklistV2(
        accountId.value,
        selectedResponsibility.value.id,
        { status: 'active' }
      );
      responsibilityCheckpoints = checklistResponse?.data || [];
    } catch (e) {
      console.warn("Could not load responsibility checklist:", e);
    }

    const toTitleCaseStatus = (s) => {
      if (!s) return 'Pending';
      return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    };

    const checklistItems = (inspectionData.value || []).map((item) => ({
      id: item.id,
      checkpoint_id: item.checkpoint_id,
      responsibility_id: selectedResponsibility.value.id,
      employee_id: computedEmployeeId.value,
      level: item.level,
      checkpoint: item.criteria || item.instructions || '',
      status: toTitleCaseStatus(item.status),
      inspected_by: item.failed_by || null,
      inspected_by_avatar: item.avatar || null,
      inspected_on: item.failed_on || null,
      reason: item.failed_reason || null,
      microlearning: item.video || null,
      microlearning_video_id: null,
    }));

    const paneMatchCtx = {
      responsibilityCode: formattedResponsibility.value?.responsibilityCode || "",
    };
    const badgeSourceList = employeeData.value?.badges?.length
      ? employeeData.value.badges
      : badges;
    const badgeRowForPane = pickBadgeForResponsibility(
      badgeSourceList,
      selectedResponsibility.value.id,
      paneMatchCtx
    );
    const badge = badgeRowForPane
      ? mapBadgeRowToInspectionChip(badgeRowForPane)
      : null;

    if (employeeBadgesRef.value && typeof employeeBadgesRef.value.handleOpenInspection === 'function') {
      employeeBadgesRef.value.handleOpenInspection({
        employee: {
          fullName: `${formattedEmployee.value.firstName || ''} ${formattedEmployee.value.lastName || ''}`.trim(),
          photoUrl: formattedEmployee.value.photoUrl || '',
          position: formattedEmployee.value.position || '',
          badge,
        },
        responsibilityAssignment: formattedResponsibility.value,
        checklistItems,
        responsibilityCheckpoints,
        responsibilityId: selectedResponsibility.value.id,
        employeeId: computedEmployeeId.value,
      });
    }
  } catch (error) {
    console.error("Error loading inspection data:", error);
    proxy.$notifications?.error("Error loading inspection data");
  } finally {
    proxy.$loader?.hide();
  }
}

async function loadInspectionData() {
  // Always initialize data first, even if some values are missing
  const extractCode = (codeStr) => {
    if (!codeStr && codeStr !== 0) return "";
    if (typeof codeStr === 'number') return `#${codeStr}`;
    const codeString = String(codeStr);
    const match = codeString.match(/#?(\d+)/);
    return match ? `#${match[1]}` : (codeString.startsWith('#') ? codeString : `#${codeString}`);
  };
  
  // Initialize with minimal data first
  const respCode = selectedResponsibility.value?.code 
    ? extractCode(selectedResponsibility.value.code) 
    : `#${selectedResponsibility.value?.id || ''}`;
  formattedResponsibility.value = {
    responsibilityCode: respCode,
    responsibilityName: selectedResponsibility.value?.name || "",
    responsibilityId: selectedResponsibility.value?.id || null,
    responsibilityImportance: 3,
    roleCode: "",
    roleName: "",
    roleId: "",
    roleImportance: 3,
    positionCode: "",
    positionName: employeeData.value?.primary_position?.name || employeeData.value?.title || "",
    positionId: "",
    positionImportance: 3,
    positionCommitment: "Primary",
    assignedOn: null,
    newDuration: 15,
    show_codes: true,
  };
  
  formattedEmployee.value = {
    firstName: employeeData.value?.first_name || "",
    lastName: employeeData.value?.last_name || "",
    position: employeeData.value?.primary_position?.name || "",
    photoUrl: employeeData.value?.avatar || employeeData.value?.photoUrl || "",
  };
  
  if (!accountId.value || !selectedResponsibility.value || !computedEmployeeId.value) {
    console.warn("Missing required data for inspection:", {
      accountId: accountId.value,
      selectedResponsibility: selectedResponsibility.value,
      computedEmployeeId: computedEmployeeId.value
    });
    // Data is already initialized above, just return
    return;
  }
  
  try {
    proxy.$loader?.show();
    
    // Update with actual data (already initialized above with minimal data)
    const respCode = selectedResponsibility.value.code 
      ? extractCode(selectedResponsibility.value.code) 
      : `#${selectedResponsibility.value.id}`;
    
    formattedResponsibility.value = {
      ...formattedResponsibility.value,
      responsibilityCode: respCode,
      responsibilityName: selectedResponsibility.value.name || "",
      responsibilityId: selectedResponsibility.value.id,
      responsibilityImportance: 3, // Default importance
      roleCode: "", // Empty if not available from frontend
      roleName: "", // Empty if not available from frontend
      roleId: "",
      roleImportance: 3,
      positionCode: "", // Empty if not available from frontend
      positionName: employeeData.value?.primary_position?.name || employeeData.value?.title || "", // Use employee's title as position name
      positionId: "",
      positionImportance: 3,
      positionCommitment: "Primary",
      assignedOn: null,
      newDuration: 15,
      show_codes: true,
    };
    
    // Update employee data (already initialized above)
    formattedEmployee.value = {
      ...formattedEmployee.value,
      firstName: employeeData.value?.first_name || "",
      lastName: employeeData.value?.last_name || "",
      position: employeeData.value?.primary_position?.name || "",
      photoUrl: employeeData.value?.avatar || employeeData.value?.photoUrl || "",
    };
    
    // Resolve current badge for this responsibility (Path / inspection parity — not strict is_current-only)
    let currentBadgeData = null;
    if (employeeData.value?.badges && Array.isArray(employeeData.value.badges)) {
      const loadMatchCtx = {
        responsibilityCode: selectedResponsibility.value?.code != null &&
          selectedResponsibility.value?.code !== ""
          ? `#${String(selectedResponsibility.value.code).replace(/^#/, "")}`
          : "",
      };
      currentBadgeData = pickBadgeForResponsibility(
        employeeData.value.badges,
        selectedResponsibility.value.id,
        loadMatchCtx
      );
    }
    
    if (currentBadgeData) {
      const badgeLevel = currentBadgeData.level || currentBadgeData.currentBadge?.level || "novice";
      currentBadgeLevel.value = typeof badgeLevel === 'string' 
        ? badgeLevel.charAt(0).toUpperCase() + badgeLevel.slice(1)
        : getLevelName(badgeLevel);
      
      const statusMap = {
        active: "Good Standing",
        good_standing: "Good Standing",
        appointed: "Appointed",
        given: "Given",
        warning: "No Dot",
        issue: "No Dot",
        obsolete: "Obsolete",
        no_dot: "No Dot",
      };
      inspectionDot.value = resolveAppointedDot(currentBadgeData, statusMap[currentBadgeData.status] || "Good Standing");
    } else {
      currentBadgeLevel.value = "Novice";
      inspectionDot.value = "Good Standing";
    }
    
    // Get responsibility details with checkpoint counts
    try {
      const respDetailResponse = await getResponsibilityIdV2(
        accountId.value,
        selectedResponsibility.value.id,
        "responsibility"
      );
      
      const respDetail = respDetailResponse.data;
      
      if (respDetail) {
        // Update responsibility code and name from detail
        formattedResponsibility.value = {
          ...formattedResponsibility.value,
          responsibilityCode: respDetail.code_str || formattedResponsibility.value.responsibilityCode,
          responsibilityName: respDetail.name || formattedResponsibility.value.responsibilityName,
        };
        
        // Update level counter from checkpoint counts (using active checkpoints)
        inspectionLevelCounter.value = {
          Apprentice: respDetail.active_apprentice_checkpoints || respDetail.count_apprentice_checkpoints || 0,
          Professional: respDetail.active_professional_checkpoints || respDetail.count_professional_checkpoints || 0,
          Coach: respDetail.active_coach_checkpoints || respDetail.count_coach_checkpoints || 0,
          Master: respDetail.active_master_checkpoints || respDetail.count_master_checkpoints || 0,
        };
        
        // Check if checkpoints exist
        const totalCheckpoints = 
          (respDetail.active_apprentice_checkpoints || 0) +
          (respDetail.active_professional_checkpoints || 0) +
          (respDetail.active_coach_checkpoints || 0) +
          (respDetail.active_master_checkpoints || 0);
        
        // If no active checkpoints, show GiveBadgeDialog directly (handled in handleResponsibilitySelected)
        // This check is done earlier in handleResponsibilitySelected before calling loadInspectionData
        
        if (totalCheckpoints > 0) {
          // Load checkpoint evaluation data for this employee
          try {
            const evalResponse = await getListActiveCheckpointsEvalV2(
              accountId.value,
              selectedResponsibility.value.id,
              computedEmployeeId.value,
              100,
              0
            );
            
            if (evalResponse.data?.results) {
              inspectionData.value = evalResponse.data.results.map((item) => ({
                id: item.checkpoint,
                checkpoint_id: item.checkpoint,
                level: getLevelName(item.proficiency_level), // Use level name string, not number
                level_name: getLevelName(item.proficiency_level),
                proficiency_level: item.proficiency_level, // Keep number for reference
                criteria: item.checkpoint_criteria,
                instructions: item.checkpoint_instructions,
                video: (item.microlearning && typeof item.microlearning === 'string' && (item.microlearning.includes('<iframe') || item.microlearning.includes('http'))) 
                  ? item.microlearning 
                  : "", // Only use if it's a valid iframe string or URL, otherwise empty string shows "No microlearning content available"
                status: resolveCheckpointOutcome(item),
                evaluation_status: resolveCheckpointOutcome(item),
                inspectionStatus: getInspectionStatusView(resolveCheckpointOutcome(item)),
                isPassed: resolveCheckpointOutcome(item) === "passed",
                isFailed: resolveCheckpointOutcome(item) === "failed",
                isNotApplicable: resolveCheckpointOutcome(item) === "not_applicable",
                failed_by: item.evaluated_by?.name || null,
                failed_on: item.evaluated_on || item.inspected_on || item.inspection_attempted_on,
                failed_reason: item.reason,
                first_name: item.evaluated_by?.first_name || item.evaluated_by?.name?.split(' ')[0] || "",
                last_name: item.evaluated_by?.last_name || item.evaluated_by?.name?.split(' ').slice(1).join(' ') || "",
                updated_on: item.evaluated_on || item.inspected_on || item.inspection_attempted_on,
                updated_by: item.evaluated_by || null,
                avatar: item.evaluator_avatar || item.evaluated_by?.picture || "",
                not_applicable_reason: resolveCheckpointOutcome(item) === "not_applicable" ? item.reason : "",
              }));
            }
            
            showNoInspectionRecords.value = inspectionData.value.length === 0;
            showInspectionPagination.value = evalResponse.data?.count > 10;
            inspectionPagination.value = {
              total: evalResponse.data?.count || 0,
              page: 1,
              perPage: 10,
            };
          } catch (evalError) {
            console.warn("Could not load checkpoint evaluation data:", evalError);
            // If evaluation fails, try to get checklist data at least
            try {
              const checklistResponse = await getResponsibilityChecklistV2(
                accountId.value,
                selectedResponsibility.value.id,
                { status: "active" }
              );
              
              if (checklistResponse.data && Array.isArray(checklistResponse.data)) {
                inspectionData.value = checklistResponse.data.map((item) => ({
                  id: item.id,
                  checkpoint_id: item.id,
                  level: getLevelName(item.proficiency_level), // Use level name string, not number
                  level_name: getLevelName(item.proficiency_level),
                  proficiency_level: item.proficiency_level, // Keep number for reference
                  criteria: item.criteria,
                  instructions: item.instructions,
                  video: (item.microlearning && typeof item.microlearning === 'string' && (item.microlearning.includes('<iframe') || item.microlearning.includes('http'))) 
                  ? item.microlearning 
                  : "", // Only use if it's a valid iframe string or URL, otherwise empty string shows "No microlearning content available"
                  status: item.status,
                  evaluation_status: "pending", // Default since no evaluation data
                  inspectionStatus: "pending-evaluation",
                  isPassed: false,
                  isFailed: false,
                  isNotApplicable: false,
                  failed_by: null,
                  failed_on: null,
                  failed_reason: null,
                  first_name: "",
                  last_name: "",
                  updated_on: null,
                  updated_by: null,
                  avatar: "",
                  not_applicable_reason: "",
                  obsolete_reason: "",
                  reset_reason: "",
                }));
              }
              
              showNoInspectionRecords.value = inspectionData.value.length === 0;
            } catch (checklistError) {
              console.warn("Could not load checklist data:", checklistError);
              inspectionData.value = [];
              showNoInspectionRecords.value = true;
            }
          }
        } else {
          // No checkpoints exist
          inspectionData.value = [];
          showNoInspectionRecords.value = true;
        }
      }
    } catch (respError) {
      console.warn("Could not load responsibility details:", respError);
      // Set empty data if API fails
      inspectionData.value = [];
      inspectionLevelCounter.value = {
        Apprentice: 0,
        Professional: 0,
        Coach: 0,
        Master: 0,
      };
      showNoInspectionRecords.value = true;
    }
    
    showInspectionPagination.value = false;
    inspectionPagination.value = {
      total: inspectionData.value.length,
      page: 1,
      perPage: 10,
    };
  } catch (error) {
    console.error("Error loading inspection data:", error);
    proxy.$notifications?.error("Error loading inspection data");
  } finally {
    proxy.$loader?.hide();
  }
}

function getLevelName(level) {
  const levelMap = {
    1: "Apprentice",
    2: "Professional",
    3: "Coach",
    4: "Master",
  };
  return levelMap[level] || "Apprentice";
}

function getInspectionStatusView(evaluationStatus) {
  if (!evaluationStatus) return "pending-evaluation";
  
  const statusMap = {
    passed: "passed-evaluation",
    failed: "failed-evaluation",
    not_applicable: "notapplicable-evaluation",
    reset: "reset-pending-evaluation",
    pending: "pending-evaluation",
  };
  
  return statusMap[evaluationStatus] || "pending-evaluation";
}

// Remove Badge Handlers
function handleRemoveBadgeClick(badgeData) {  
  if (!badgeData || !badgeData.id) {
    proxy.$notifications?.error("Invalid badge data");
    return;
  }

  const badgeLevel = badgeData.currentBadge?.level || badgeData.level || "";
  
  selectedBadgeForRemoval.value = {
    id: badgeData.id,
    badgeName: getBadgeLevelName(badgeLevel),
    level: badgeLevel,
    responsibilityName: badgeData.responsibility?.name || "",
    responsibility: badgeData.responsibility || null,
  };
  
  showRemoveBadgeDialog.value = true;
}

function getBadgeLevelName(level) {
  if (typeof level === 'string') {
    return level;
  }
  const levelMap = {
    1: "Apprentice",
    2: "Professional",
    3: "Coach",
    4: "Master",
  };
  return levelMap[level] || "Apprentice";
}

async function handleConfirmRemoveBadge({ reason: removeReason }) {
  if (!selectedBadgeForRemoval.value || !selectedBadgeForRemoval.value.id || !removeReason?.trim()) {
    proxy.$notifications?.error("Please provide a reason for removing the badge");
    return;
  }

  proxy.$loader?.show();
  try {
    const payload = {
      badge: selectedBadgeForRemoval.value.id,
      method: "removed",
      reason: removeReason.trim(),
    };

    await deleteResponsibilityBadge(
      accountId.value,
      selectedBadgeForRemoval.value.id,
      payload
    );

    proxy.$notifications?.success("Badge removed successfully");
    
    // Close inspection form if it's open (badge might have been removed from inspection form)
    if (showInspectForm.value) {
      handleCloseInspection();
    }
    
    // Navigate to badges tab
    selectedTab.value = "badges";
    
    // Update route query parameter to reflect badges tab
    if (proxy.$router && route) {
      const isNewOrganization = route?.name === "NewOrganization" || 
                                 route?.path?.includes("/organization");
      
      if (isNewOrganization) {
        const employeeId = computedEmployeeId.value || route?.query?.employeeId;
        const query = {
          employeeId: employeeId,
          employeeTab: "badges",
        };
        if (route?.query?.fromReadiness === "1") {
          query.fromReadiness = "1";
        }
        proxy.$router
          .replace({
            name: "NewOrganization",
            params: { tab: "employees" },
            query,
          })
          .catch(() => {});
      } else {
        proxy.$router
          .replace({
            ...route,
            query: {
              ...route.query,
              employeeTab: "badges",
            },
          })
          .catch(() => {});
      }
    }
    
    // Refresh employee data and badge list so UI updates in real time
    await fetchEmployeeData();
    handleEmployeeUpdate();
    await refreshBadgeList();
    
    showRemoveBadgeDialog.value = false;
    selectedBadgeForRemoval.value = null;
  } catch (error) {
    console.error("Error removing badge:", error);
    const errorMessage = error?.response?.data?.message || error?.response?.data?.level?.[0] || error?.message || "Error removing badge";
    proxy.$notifications?.error(errorMessage);
  } finally {
    proxy.$loader?.hide();
  }
}

function handleCloseRemoveBadgeDialog() {
  showRemoveBadgeDialog.value = false;
  selectedBadgeForRemoval.value = null;
}

// Refresh badge list helper function
async function refreshBadgeList() {
  try {
    // Access EmployeeBadges component via ref and call refresh method
    if (employeeBadgesRef.value && typeof employeeBadgesRef.value.refreshBadges === 'function') {
      await employeeBadgesRef.value.refreshBadges();
    }
  } catch (error) {
    console.warn("Could not refresh badge list:", error);
    // Don't show error to user, just log it
  }
}

// Inspection Form Handlers
function handleInspectionBreadcrumbClick(breadcrumb) {
  if (breadcrumb.text === "Employee") {
    handleCloseInspection();
    // Navigate back to badges tab
    selectedTab.value = "badges";
  }
}

function handleBadgeSelectedFromInspection(payload) {
  if (!accountId.value || !computedEmployeeId.value || !selectedResponsibility.value) {
    return;
  }

  // Remove badge: open remove flow only
  if (payload?.can_remove || payload?.isRemoveAction) {
    const currentBadgeData = employeeData.value?.badges?.find(
      (badge) => {
        const badgeRespId = badge.issued_for?.id || badge.responsibility?.id || badge.responsibility_id;
        return badgeRespId == selectedResponsibility.value.id && badge.is_current;
      }
    );
    if (currentBadgeData) {
      handleRemoveBadgeClick(currentBadgeData);
    }
    return;
  }

  let levelString = payload?.value || payload?.Label || payload?.level || (typeof payload === "string" ? payload : null);
  if (!levelString) {
    proxy.$notifications?.error("Invalid badge selection");
    return;
  }
  const level = String(levelString).toLowerCase().trim();
  giveBadgeDialogLockedLevel.value = level;
  showGiveBadgeDialog.value = true;
}

function getBadgeLevelNumber(level) {
  if (!level || typeof level !== 'string') {
    console.error("Invalid level provided:", level);
    return 1; // Default to Apprentice
  }
  
  const levelMap = {
    apprentice: 1,
    proficient: 2,
    coach: 3,
    architect: 4,
  };
  
  const normalizedLevel = normalizeBadgeLevel(level);
  return levelMap[normalizedLevel] || 1;
}

function handleInspectionSearch(searchTerm) {
  inspectionFilterText.value = searchTerm;
}

function handleInspectionClick(event) {
  // Handle inspection click events
}

function handleInspectionRemoveFilter(filter) {
  inspectionChipText.value = inspectionChipText.value.filter(
    (c) => c !== filter
  );
}

function handleInspectionFilterTypes(filterType) {
  // inspectionFilterType.value = filterType; // Remove this as it overwrites the title with an array of levels
}

function handleInspectionPagination(pagination) {
  inspectionPagination.value = pagination;
}

function handleInspectionClearSelection() {
  inspectionChipText.value = [];
}

function handleInspectionCheckedItemsChanged(items) {
  inspectionChipText.value = items
    .map((item) => (typeof item === "string" ? item : item.value))
    .filter(Boolean);
}

// Handle Pass
async function handleInspectionPass(row) {
  try {
    proxy.$loader?.show();
    
    const chkpId = row.id || row.checkpoint_id || row.checkpoint;
    const previousReadyLevel = readyForBadgeLevel.value;
    
    const dataPayload = {
      employee: computedEmployeeId.value,
      checkpoint: chkpId,
      reason: "passed",
      status: "passed",
    };
    
    await passCheckPointEvaluation(
      accountId.value,
      selectedResponsibility.value.id,
      chkpId,
      dataPayload
    );
    
    // Reload checkpoint data
    await reloadInspectionData();
    
    // Check if readyForLevel changed (employee is ready for next badge) – show Award Badge dialog
    const newReadyLevel = readyForBadgeLevel.value;
    if (newReadyLevel && newReadyLevel !== previousReadyLevel) {
      awardBadgeLevel.value = newReadyLevel;
      showAwardBadgeDialog.value = true;
    } else {
      proxy.$notifications?.success("Checkpoint passed successfully");
    }
  } catch (error) {
    const msg =
      proficiencyCheckpointErrorMessage(error) || "Error passing checkpoint";
    proxy.$notifications?.error(msg);
    console.error("Error passing checkpoint:", error);
  } finally {
    proxy.$loader?.hide();
  }
}

// Handle Fail - opens reason modal first
function handleInspectionFail(row) {
  selectedCheckpoint.value = row;
  reason.value = row.failed_reason && row.evaluation_status === "failed" 
    ? row.failed_reason 
    : "";
  openReasonModal("fail");
}

// Handle Not Applicable - opens reason modal first
function handleInspectionNotApplicable(row) {
  selectedCheckpoint.value = row;
  reason.value = "";
  openReasonModal("notApplicable");
}

// Reason Modal handlers
function openReasonModal(type) {
  reasonModal.value.type = type;
  reasonModal.value.config = modalConfigs[type];
  reasonModal.value.show = true;
}

function closeReasonModal() {
  reasonModal.value.show = false;
  reasonModal.value.type = null;
  reasonModal.value.config = {};
  reason.value = "";
  selectedCheckpoint.value = null;
}

async function handleReasonAction() {
  if (reasonModal.value.type === "fail") {
    await executeFail(selectedCheckpoint.value);
  } else if (reasonModal.value.type === "notApplicable") {
    await executeNotApplicable(selectedCheckpoint.value);
  }
}

// Execute Fail API call
async function executeFail(checkpointData) {
  if (!reason.value || reason.value.trim() === "") {
    proxy.$notifications?.error("Please provide a reason");
    return;
  }
  
  try {
    proxy.$loader?.show();
    
    const chkpId = checkpointData.id || checkpointData.checkpoint_id || checkpointData.checkpoint;
    
    const dataPayload = {
      employee: computedEmployeeId.value,
      checkpoint: chkpId,
      reason: reason.value,
      status: "failed",
    };
    
    await failCheckPointEvaluation(
      accountId.value,
      selectedResponsibility.value.id,
      chkpId,
      dataPayload
    );
    
    closeReasonModal();
    
    // Reload checkpoint data
    await reloadInspectionData();
    
    proxy.$notifications?.success("Checkpoint marked as failed");
  } catch (error) {
    closeReasonModal();
    const msg =
      proficiencyCheckpointErrorMessage(error) || "Error failing checkpoint";
    proxy.$notifications?.error(msg);
    console.error("Error failing checkpoint:", error);
  } finally {
    proxy.$loader?.hide();
  }
}

// Execute Not Applicable API call
async function executeNotApplicable(checkpointData) {
  if (!reason.value || reason.value.trim() === "") {
    proxy.$notifications?.error("Please provide a reason");
    return;
  }
  
  try {
    proxy.$loader?.show();
    
    const chkpId = checkpointData.id || checkpointData.checkpoint_id || checkpointData.checkpoint;
    
    const dataPayload = {
      employee: computedEmployeeId.value,
      checkpoint: chkpId,
      reason: reason.value,
      status: "not_applicable",
    };
    
    await notApplicableCheckPointEvaluation(
      accountId.value,
      selectedResponsibility.value.id,
      chkpId,
      dataPayload
    );

    const previousReadyLevel = readyForBadgeLevel.value;

    closeReasonModal();

    // Reload checkpoint data
    await reloadInspectionData();

    const newReadyLevel = readyForBadgeLevel.value;
    if (newReadyLevel && newReadyLevel !== previousReadyLevel) {
      awardBadgeLevel.value = newReadyLevel;
      showAwardBadgeDialog.value = true;
    } else {
      proxy.$notifications?.success("Checkpoint marked as not applicable");
    }
  } catch (error) {
    closeReasonModal();
    const msg =
      proficiencyCheckpointErrorMessage(error) ||
      "Error marking checkpoint as not applicable";
    proxy.$notifications?.error(msg);
    console.error("Error marking checkpoint as not applicable:", error);
  } finally {
    proxy.$loader?.hide();
  }
}

// Reload inspection data after checkpoint evaluation
async function reloadInspectionData() {
  if (!accountId.value || !selectedResponsibility.value || !computedEmployeeId.value) return;
  
  try {
    // Reload checkpoint evaluation data
    const evalResponse = await getListActiveCheckpointsEvalV2(
      accountId.value,
      selectedResponsibility.value.id,
      computedEmployeeId.value,
      100,
      0
    );
    
    if (evalResponse.data?.results) {
      inspectionData.value = evalResponse.data.results.map((item) => ({
        id: item.checkpoint,
        checkpoint_id: item.checkpoint,
        level: getLevelName(item.proficiency_level), // Use level name string, not number
        level_name: getLevelName(item.proficiency_level),
        proficiency_level: item.proficiency_level, // Keep number for reference
                criteria: item.checkpoint_criteria,
                instructions: item.checkpoint_instructions,
                video: (item.microlearning && typeof item.microlearning === 'string' && (item.microlearning.includes('<iframe') || item.microlearning.includes('http'))) 
                  ? item.microlearning 
                  : "", // Only use if it's a valid iframe string or URL, otherwise empty string shows "No microlearning content available"
                status: resolveCheckpointOutcome(item),
                evaluation_status: resolveCheckpointOutcome(item),
                inspectionStatus: getInspectionStatusView(resolveCheckpointOutcome(item)),
                isPassed: resolveCheckpointOutcome(item) === "passed",
                isFailed: resolveCheckpointOutcome(item) === "failed",
                isNotApplicable: resolveCheckpointOutcome(item) === "not_applicable",
                failed_by: item.evaluated_by?.name || null,
                failed_on: item.evaluated_on || item.inspected_on || item.inspection_attempted_on,
                failed_reason: item.reason,
                first_name: item.evaluated_by?.first_name || item.evaluated_by?.name?.split(' ')[0] || "",
                last_name: item.evaluated_by?.last_name || item.evaluated_by?.name?.split(' ').slice(1).join(' ') || "",
                updated_on: item.evaluated_on || item.inspected_on || item.inspection_attempted_on,
                updated_by: item.evaluated_by || null,
                avatar: item.evaluator_avatar || item.evaluated_by?.picture || "",
                not_applicable_reason: resolveCheckpointOutcome(item) === "not_applicable" ? item.reason : "",
                obsolete_reason: "",
                reset_reason: "",
              }));
    }
    
    // Also reload responsibility details to get updated readyForLevel
    const respDetailResponse = await getResponsibilityIdV2(
      accountId.value,
      selectedResponsibility.value.id,
      "responsibility"
    );
    
    // Try to get updated badge info (this might need employee badges API)
    // For now, we'll keep the current badge level
  } catch (error) {
    console.warn("Error reloading inspection data:", error);
  }
}

// When user clicks Reset on a checkpoint, table emits onClose with checkpoint data.
// Show reset confirmation instead of closing the form.
function handleInspectionResetOrClose(row) {
  if (row && (row.id || row.checkpoint_id || row.checkpoint)) {
    checkpointToReset.value = row;
    showResetConfirmDialog.value = true;
    return;
  }
  handleCloseInspection();
}

async function handleConfirmResetEvaluation() {
  if (!checkpointToReset.value || !accountId.value || !selectedResponsibility.value || !computedEmployeeId.value) {
    showResetConfirmDialog.value = false;
    checkpointToReset.value = null;
    return;
  }
  try {
    proxy.$loader?.show();
    const chkpId = checkpointToReset.value.id || checkpointToReset.value.checkpoint_id || checkpointToReset.value.checkpoint;
    const dataPayload = {
      employee: computedEmployeeId.value,
      checkpoint: chkpId,
      reason: "Reset by coach",
      status: "reset",
    };
    await resetCheckPointEvaluation(
      accountId.value,
      selectedResponsibility.value.id,
      chkpId,
      dataPayload
    );
    showResetConfirmDialog.value = false;
    checkpointToReset.value = null;
    await reloadInspectionData();
    proxy.$notifications?.success("Evaluation reset successfully");
  } catch (error) {
    console.error("Error resetting checkpoint:", error);
    proxy.$notifications?.error("Error resetting evaluation");
  } finally {
    proxy.$loader?.hide();
    showResetConfirmDialog.value = false;
    checkpointToReset.value = null;
  }
}

function handleCloseInspection() {
  showInspectForm.value = false;
  selectedResponsibility.value = null;
  inspectionData.value = [];
  inspectionFilterText.value = "";
  formattedResponsibility.value = null;
  formattedEmployee.value = null;
  currentBadgeLevel.value = null;
  inspectionDot.value = "Good Standing";
  showAwardBadgeDialog.value = false;
  awardBadgeLevel.value = "";
  // Return to badges tab
  selectedTab.value = "badges";
}

// Award Badge Dialog handlers (when employee becomes ready for level after passing inspection)
function closeAwardBadgeDialog() {
  showAwardBadgeDialog.value = false;
  awardBadgeLevel.value = "";
}

function skipAwardBadge() {
  closeAwardBadgeDialog();
}

async function activateAwardBadge(isNotify) {
  try {
    proxy.$loader?.show();
    const level = getBadgeLevelNumber(awardBadgeLevel.value);
    const badgePayload = {
      employee: computedEmployeeId.value,
      responsibility: selectedResponsibility.value.id,
      level: level,
      given: false,
      issued_method: "awarded",
      notify: isNotify || false,
    };
    await createProficiencyBadge(accountId.value, badgePayload);
    closeAwardBadgeDialog();
    // Close inspection and go to badges listing so UI updates in real time
    handleCloseInspection();
    await fetchEmployeeData();
    handleEmployeeUpdate();
    await refreshBadgeList();
    proxy.$notifications?.success("Badge awarded successfully");
  } catch (error) {
    console.error("Error awarding badge:", error);
    proxy.$notifications?.error("Error awarding badge");
  } finally {
    proxy.$loader?.hide();
  }
}

// Give Badge Dialog Handlers
function handleOpenGiveBadgeDialogFromBadgesInspection(payload) {
  const level = (payload?.level || payload?.defaultLevel || "apprentice")
    .toString()
    .toLowerCase();
  const defaultLevel = (payload?.defaultLevel || level).toString().toLowerCase();
  giveBadgeDialogLockedLevel.value = level;
  selectedBadgeLevel.value = defaultLevel;
  giveBadgeOpenedFromBadgesInspection.value = true;
  showGiveBadgeDialog.value = true;
}

function handleCloseGiveBadge() {
  const fromInspection = giveBadgeOpenedFromBadgesInspection.value;
  showGiveBadgeDialog.value = false;
  giveBadgeDialogLockedLevel.value = null;
  giveBadgeOpenedFromBadgesInspection.value = false;
  if (!fromInspection) {
    selectedResponsibility.value = null;
  }
  selectedBadgeLevel.value = "apprentice";
  appointMode.value = false;
}


const employeeFullName = computed(() => {
  return employeeData.value?.first_name + " " + employeeData.value?.last_name || "";
});
const toastMessage = (payload) => {
  proxy.$notifications?.success(`
    Badge 
    ${payload.level} 
    was successfully issued to
     ${employeeFullName.value}
      for responsibility
       ${selectedResponsibility.value.name}.
       `);
};

function handleAppointBadge(payload) {
  return handleGiveBadge(payload, true);
}

async function handleGiveBadge(payload, appoint = false) {
  if (!accountId.value || !computedEmployeeId.value || !selectedResponsibility.value) {
    return;
  }
  
  try {
    proxy.$loader?.show();
    
    // Convert level string to integer (API requires integer: 1=Apprentice, 2=Professional, 3=Coach, 4=Master)
    const levelNumber = getBadgeLevelNumber(payload.level);
    
    if (!levelNumber || levelNumber === 0) {
      proxy.$notifications?.error("Invalid badge level selected");
      proxy.$loader?.hide();
      return;
    }
    
    const badgePayload = appoint
      ? buildAppointBadgePayload({
          employeeId: computedEmployeeId.value,
          responsibilityId: selectedResponsibility.value.id,
          level: payload.level,
          notify: payload.notifyEmployee,
        })
      : {
          employee: computedEmployeeId.value,
          responsibility: selectedResponsibility.value.id,
          level: levelNumber, // Use integer instead of string
          given: true,
          issued_method: "given",
          notify: payload.notifyEmployee || false,
        };
    
    const createRes = await createProficiencyBadge(accountId.value, badgePayload);
    const newBadgeId = extractCreatedProficiencyBadgeId(createRes);

    if (appoint) {
      proxy.$notifications?.success("Badge appointed successfully");
    } else {
      toastMessage(payload);
    }
    appointMode.value = false;

    const fromInspection = giveBadgeOpenedFromBadgesInspection.value;

    // Close dialog and clear locked level
    showGiveBadgeDialog.value = false;
    giveBadgeDialogLockedLevel.value = null;
    if (fromInspection) {
      giveBadgeOpenedFromBadgesInspection.value = false;
    } else {
      selectedResponsibility.value = null;
    }

    if (fromInspection) {
      employeeBadgesRef.value?.bumpInspectionChipAfterGive?.({
        badgeId: newBadgeId,
        level: payload.level,
      });
      await employeeBadgesRef.value?.reloadInspectionAfterGiveBadge?.({
        skipInspectionBadgeSync: true,
      });
    }

    await fetchEmployeeData();
    handleEmployeeUpdate();

    if (!fromInspection) {
      await refreshBadgeList();
    }

  } catch (error) {
    console.error("Error giving badge:", error);
    proxy.$notifications?.error(appoint ? "Error appointing badge" : "Error issuing badge");
  } finally {
    proxy.$loader?.hide();
  }
}

function closeUpgradeBadgeModal() {
  showUpgradeBadgeDialog.value = false;
  selectedResponsibilityForUpgrade.value = null;
}

async function handleGiveBadgeFromUpgradeDialog(payload) {
  const empId = computedEmployeeId.value;
  const resp = selectedResponsibilityForUpgrade.value;
  if (!accountId.value || !empId || !resp?.id) return;
  try {
    proxy.$loader?.show();
    const levelNumber = getBadgeLevelNumber(payload?.level);
    const badgePayload = {
      employee: empId,
      responsibility: resp.id,
      level: levelNumber,
      given: true,
      issued_method: "given",
      notify: payload?.notifyEmployee || false,
    };
    await createProficiencyBadge(accountId.value, badgePayload);
    closeUpgradeBadgeModal();
    await fetchEmployeeData();
    handleEmployeeUpdate();
    await refreshBadgeList();
    toastMessage(payload);
  } catch (error) {
    console.error("Error issuing upgrade badge:", error);
    proxy.$notifications?.error("Error issuing badge");
  } finally {
    proxy.$loader?.hide();
  }
}

// Watch for employee data updates from store (e.g., after readiness recalculation, status change, or position assignment)
watch(
  () => employeeStore.currentEmployee,
  (newEmployee) => {
    if (newEmployee && newEmployee.id == computedEmployeeId.value) {
      // Normalize status to ensure both status and current_status are set
      const normalizedEmployee = {
        ...newEmployee,
        status: newEmployee.status || newEmployee.current_status,
        current_status: newEmployee.current_status || newEmployee.status,
      };
      
      // Replace entire object to ensure deep reactivity (especially for nested properties like primary_position)
      // This ensures changes to primary_position are properly detected
      employeeData.value = { ...normalizedEmployee };
      // Do not invoke child handleEmployeeStatusChanged from this watcher —
      // child already reacts to store updates; calling it caused GET storms (ER-9611).
    }
  },
  { deep: true, immediate: true }
);

// Explicitly watch for primary_position changes to ensure reactivity
watch(
  () => employeeStore.currentEmployee?.primary_position,
  (newPosition) => {
    const storeEmployee = employeeStore.currentEmployee;
    if (storeEmployee && storeEmployee.id == computedEmployeeId.value && employeeData.value) {
      // Update primary_position in local employeeData to trigger reactivity
      employeeData.value = {
        ...employeeData.value,
        primary_position: newPosition ? { ...newPosition } : null,
      };
    }
  },
  { deep: true }
);

// Watch for employeeId changes and refetch data
watch(
  computedEmployeeId,
  async (newEmployeeId, oldEmployeeId) => {
    if (newEmployeeId && oldEmployeeId && newEmployeeId !== oldEmployeeId && accountId.value) {
      try {
        proxy.$loader?.show();
        employeeId.value = newEmployeeId;
        employeeData.value = null;
        selectedTab.value = getOrganizationEmployeeDefaultQueryTab();
        await fetchEmployeeData();
      } catch (error) {
        console.error("Error fetching employee data on change:", error);
      } finally {
        proxy.$loader?.hide();
      }
    }
  },
  { immediate: false }
);

// Also watch route query for employeeId changes
watch(
  () => proxy?.$route?.query?.employeeId,
  async (newEmployeeId, oldEmployeeId) => {
    if (newEmployeeId && oldEmployeeId && newEmployeeId !== oldEmployeeId && accountId.value) {
      try {
        proxy.$loader?.show();
        employeeId.value = newEmployeeId;
        employeeData.value = null;
        
        applyOrganizationEmployeeQueryTab(proxy?.$route?.query?.employeeTab);
        
        await fetchEmployeeData();
      } catch (error) {
        console.error("Error fetching employee data on route change:", error);
      } finally {
        proxy.$loader?.hide();
      }
    }
  },
  { immediate: false }
);

// Watch for employeeTab query parameter changes to sync selected tab
watch(
  () => proxy?.$route?.query?.employeeTab,
  (newTab) => {
    if (!organizationEmployeeQueryTabsReady.value) {
      return;
    }
    syncEmployeeTabFromQuery(newTab);
  },
  { deep: true }
);

// Watch for employeeId changes to reset tab when switching employees
watch(
  () => computedEmployeeId.value,
  (newEmployeeId, oldEmployeeId) => {
    if (newEmployeeId && newEmployeeId !== oldEmployeeId) {
      // When switching to a new employee, check route for tab preference
      const employeeTabFromQuery = proxy?.$route?.query?.employeeTab;
      if (employeeTabFromQuery) {
        applyOrganizationEmployeeQueryTab(employeeTabFromQuery);
      } else {
        selectedTab.value = getOrganizationEmployeeDefaultQueryTab();
      }
    }
  }
);

onMounted(async () => {
  try {
    proxy.$loader?.show();
    accountId.value = JSON.parse(localStorage.getItem("currentAccountID"));
    employeeId.value = computedEmployeeId.value;

    // Dashboard tab parity with the route-param path — gated by the same
    // idea-MDP-609 flag that hides/shows the tab there (ER-11912 / ER-11913).
    showDashboardTab.value =
      (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_609, false)) === true;
    organizationEmployeeQueryTabsReady.value = true;

    // Initialize tab from query parameter after idea-MDP-609 is loaded
    syncEmployeeTabFromQuery(proxy?.$route?.query?.employeeTab);

    await fetchEmployeeData();
  } finally {
    proxy.$loader?.hide();
  }
});
</script>

<style scoped>
.employee-detail {
  background: #fff;
  border-radius: 8px;
  width: 100%;
  border: 1px solid #eaecf0;
  box-shadow: 0px 1px 2px 0px #1018280f, 0px 1px 3px 0px #1018281a;
  border-radius: 8px;
  background: #fff;
  width: 100%;
  border: 1px solid #eaecf0;
}

.employee-page-header .employee-card-header-chatter-link {
  left: unset !important;
  top: unset !important;
}

.employee-detail-content {
  overflow: visible;
  height: auto;
}

.tab-content-wrapper {
  width: 100%;
}

/* Hide ALL headers in child views - be very aggressive */
.tab-content-wrapper ::v-deep .header-wrapper,
.tab-content-wrapper
  ::v-deep
  .employee-card-path-to-readiness
  > .header-wrapper,
.tab-content-wrapper ::v-deep .v-container .header-wrapper,
.tab-content-wrapper
  ::v-deep
  .employee-card-path-to-readiness
  > *:first-child.header-wrapper {
  display: none !important;
  visibility: hidden !important;
  height: 0 !important;
  overflow: hidden !important;
}

/* Remove v-container padding and make it full width */
.tab-content-wrapper ::v-deep .v-container {
  padding: 0 !important;
  margin: 0 !important;
  max-width: 100% !important;
  width: 100% !important;
}

/* Hide PageTitle components */
.tab-content-wrapper ::v-deep PageTitle {
  display: none !important;
}

/* Remove padding from content wrappers */
.tab-content-wrapper ::v-deep .employee-card-path-to-readiness {
  padding: 0 !important;
  margin: 0 !important;
}

.tab-content-wrapper ::v-deep .custom-path {
  padding: 0 !important;
  margin: 0 !important;
}

.tab-content-wrapper ::v-deep .emp-assignments-view {
  padding: 0 !important;
  margin: 0 !important;
}

/* Inspect Form Page Styles */
.inspect-form-page-wrapper {
  width: 100%;
  margin-top: 16px;
}
</style>

<style>
.employee-page-header .employee-card-header-chatter-link {
  position: unset !important;
}

.employee-page-header {
  padding: 16px !important;
}
</style>
